import React from "react";
import { v4 as uuidV4 } from "uuid";

import { Link } from "react-router-dom";

import cloneDeep from "lodash.clonedeep";
import { format } from "date-fns";
import { ScrollEnable } from "../../../helpers/ScrollLock";

import { getImageBaseUrl } from "../../../apis/apiSettings";

import Thumbnail from "./Thumbnail";

// Icons
import { ReactComponent as PhotoIcon } from "../Icons/photo_48.svg";
import { ReactComponent as ComparisonIcon } from "../Icons/comparison_48.svg";
import { ReactComponent as PsoriasisCalculationIcon } from "../Icons/psoriasis_calculation_48.svg";
import { ReactComponent as PsoriasisSegmentationIcon } from "../Icons/psoriasis_segmentation_48.svg";
import { ReactComponent as BodySegmentationIcon } from "../Icons/body_segmentation_48.svg";
import {
  CalendarDaysIcon,
  PencilSquareIcon,
} from "@heroicons/react/24/outline";

import { useDispatch } from "react-redux";
import {
  setPatientId,
  setPatientUniqueID,
  setPatientFullName,
} from "../../../redux/features/patientState/patientStateSlice";

import {
  setMenuOpen,
  setActiveMenu,
  setActivePage,
  setActivePatientSidebarGroup,
} from "../../../redux/features/applicationState/applicationStateSlice";

// Entry Icons
const EntryIcons = {
  photo: <PhotoIcon />,
  comparison: <ComparisonIcon />,
  psoriasis: <PsoriasisCalculationIcon />,
  psoriasis_segmentation: <PsoriasisSegmentationIcon />,
  segmentation: <BodySegmentationIcon />,
};

const entryNames = {
  photo: "Photo",
  comparison: "Comparison",
  psoriasis: "Psoriasis Calculation",
  psoriasis_segmentation: "Psoriasis Segmentation Calculation",
  segmentation: "Body Part Segmentation",
};

const entrySidebarGroups = {
  photo: "photo",
  comparison: "photo",
  psoriasis: "psoriasisAiAssisted",
  psoriasis_segmentation: "psoriasisAiAssisted",
  segmentation: "aiAssisted",
};

const EntryRow = ({ row }) => {
  const dispatch = useDispatch();

  const isHomeRow = row.is_home_row ? row.is_home_row : false;
  const isAllEntry = row.is_all_entry ? row.is_all_entry : false;

  const entryId = !isHomeRow && !isAllEntry ? row.id : row.original_entry_id;
  const entryType = row.entry_type;
  const entryTypeName = entryNames[entryType];

  const appointmentDate = row.appointment_date;
  const dateModified = row.date_modified;

  const patientId = row.patient_owner;
  // Do not extract other details about patient unless on Home Screen.
  const patientUniqueId = isHomeRow ? row.unique_id : "";
  const patientFullName = isHomeRow ? row.patient_owner_full_name : "";

  const tags = row.classification_tags;
  const summary = row.summary;
  const thumbnail = row.thumbnail;

  const handlePatientSelection = () => {
    dispatch(setActivePage(`/entries/${entryType}_menu/${patientId}`));
    dispatch(setMenuOpen(true));
    dispatch(setActiveMenu("patients"));
    dispatch(setActivePatientSidebarGroup(entrySidebarGroups[entryType]));

    // Only set patient when selecting entry from Home Screen. In other cases the patient is already set.
    if (isHomeRow) {
      dispatch(setPatientId(patientId));
      dispatch(setPatientFullName(patientFullName));
      dispatch(setPatientUniqueID(patientUniqueId));
    }
  };

  return (
    <>
      <Link
        to={{ pathname: `/entries/${entryType}_entry/${entryId}` }}
        onClick={() => {
          handlePatientSelection();
        }}
      >
        <div
          className="flex flex-col md:flex-row items-start justify-between w-full h-full p-4 Xbg-orange-500
            border border-neutral-300 rounded-xl
          
            cursor-pointer select-none
            hover:scale-[101%] active:scale-100 transition-all duration-200
            hover:border-neutral-800
            "
        >
          {/* Data container */}
          <div className="flex flex-1 flex-row items-start justify-start h-full Xbg-orange-100">
            <div className="flex flex-1 flex-col items-start justify-start text-neutral-800 Xbg-orange-500">
              {/* Only show entry type when on Home Screen or when in All Entries view */}
              {(isHomeRow || isAllEntry) && (
                <>
                  {/* Entry type */}
                  <div className="flex flex-row items-center justify-start w-full Xbg-red-500">
                    <p className="text-2xl font-medium">{entryTypeName}</p>
                    <div className="px-1" />
                    <div className="h-8 w-8">{EntryIcons[entryType]}</div>
                  </div>
                  <div className="py-1" />
                </>
              )}

              {/* Appointment Date and Date Modified */}
              <div className="">
                <div className="flex flex-row items-start text-neutral-800">
                  <CalendarDaysIcon className="h-7 w-7 min-h-[1.75rem] min-w-[1.75rem] mr-2" />
                  <p
                    className={`${
                      !isHomeRow && !isAllEntry ? "text-xl" : "text-lg"
                    } font-normal`}
                  >
                    Appointment:{" "}
                    {format(
                      new Date(appointmentDate),
                      " EEEE, MMMM dd, yyyy', at' h:mm a"
                    )}
                  </p>
                </div>
                <div className="py-1" />
                <div className="flex flex-row items-start text-neutral-500">
                  <PencilSquareIcon className="h-7 w-7 min-h-[1.75rem] min-w-[1.75rem] mr-2" />
                  <p className="text-base font-normal">
                    Modified:{" "}
                    {format(
                      new Date(dateModified),
                      " EEEE, MMMM dd, yyyy', at' h:mm a"
                    )}
                  </p>
                </div>
              </div>

              {/* Entry UUID */}
              <div className="py-1" />
              <div>
                <p className="text-sm text-neutral-400">{entryId}</p>
              </div>

              {/* Only show Patient Name and Unique ID on Home Screen  */}
              {isHomeRow && (
                <>
                  <div className="py-1" />
                  <div className="flex flex-row w-full items-center flex-shrink justify-start Xbg-purple-500">
                    {/* Patient Full Name */}

                    <div className="flex flex-row max-w-[50%] h-full p-2 bg-cyan-200 rounded-md">
                      <p className="">
                        {patientFullName ? patientFullName : "Nameless Patient"}
                      </p>
                    </div>

                    <div className="px-1" />
                    {/* Patient Unique ID */}

                    <div className="flex flex-row max-w-[50%] h-full p-2 bg-green-200 rounded-md">
                      <p className="break-all">{patientUniqueId}</p>
                    </div>
                  </div>
                </>
              )}

              {/* Tags */}
              <div className="flex flex-row items-start justify-start w-full flex-wrap pt-2">
                {tags.map((tag, index) => (
                  <div key={`${tag}-${index}`} className="pr-2 pb-2">
                    <div className="flex flex-row items-center justify-start p-2 rounded-md bg-yellow-100">
                      <div className="text-base font-medium text-neutral-900">
                        {tag}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              {/* Summary */}
              {summary && (
                <div className="flex flex-row p-2 bg-blue-100 rounded-md">
                  <p>{summary}</p>
                </div>
              )}
            </div>
          </div>
          {/* Image container */}
          <div className="flex flex-1 flex-row items-center justify-center md:justify-end w-full h-full pt-4 md:pt-0 Xbg-blue-100">
            <Thumbnail
              thumbnail={thumbnail}
              imageStyle={
                "md:h-64 md:w-64 outline outline-1 outline-neutral-200 rounded-lg"
              }
            />
          </div>
        </div>
      </Link>

      {/* <div className="">{JSON.stringify(row)}</div> */}
    </>
  );
};

const PatientRow = ({ row, additionalArgs }) => {
  const dispatch = useDispatch();

  const {
    isRapidEntry,
    selectedExistingPatientId,
    setSelectedExistingPatientId,
  } = additionalArgs;

  const patientId = row.id;
  const patientUniqueId = row.unique_id;
  const patientFullName = row.full_name;

  const dateModified = row.date_modified;
  const lastAppointmentDate = row.last_appointment_date;

  const tags = row.classification_tags;
  const thumbnail = row.thumbnail;

  const pageLink = `/entries/all_entries_menu/${patientId}`;

  const handlePatientSelection = () => {
    if (isRapidEntry) {
      setSelectedExistingPatientId(patientId);
    } else {
      dispatch(setActivePage(pageLink));
      dispatch(setMenuOpen(true));
      dispatch(setActiveMenu("patients"));
      dispatch(setActivePatientSidebarGroup("photo"));
      dispatch(setPatientId(patientId));
      dispatch(setPatientFullName(patientFullName));
      dispatch(setPatientUniqueID(patientUniqueId));
    }
  };

  const buttonContent = (
    <div
      className={`flex flex-col md:flex-row items-start justify-between w-full h-full p-4 Xbg-orange-500
      border border-neutral-300 rounded-xl
    
      cursor-pointer select-none
      hover:scale-[101%] active:scale-100 transition-all duration-200
      hover:border-neutral-800

      ${
        patientId === selectedExistingPatientId
          ? "bg-violet-50 border-neutral-500"
          : ""
      }
      `}
      onClick={() => {
        handlePatientSelection();
      }}
    >
      {/* Data container */}
      <div className="flex flex-1 flex-row items-start justify-start h-full Xbg-orange-100">
        <div className="flex flex-1 flex-col items-start justify-start text-neutral-800 Xbg-orange-500">
          {/* Last Appointment Date and Date Modified */}
          <div className="">
            <div className="flex flex-row items-start text-neutral-800">
              <CalendarDaysIcon className="h-7 w-7 min-h-[1.75rem] min-w-[1.75rem] mr-2" />
              <p className="text-lg lg:text-xl font-normal">
                Last Appointment:{" "}
                {format(
                  new Date(lastAppointmentDate),
                  " EEEE, MMMM dd, yyyy', at' h:mm a"
                )}
              </p>
            </div>
            <div className="py-1" />
            <div className="flex flex-row items-start text-neutral-500">
              <PencilSquareIcon className="h-7 w-7 min-h-[1.75rem] min-w-[1.75rem] mr-2" />
              <p className="text-md lg:text-lg font-normal">
                Modified:{" "}
                {format(
                  new Date(dateModified),
                  " EEEE, MMMM dd, yyyy', at' h:mm a"
                )}
              </p>
            </div>
          </div>

          {/* Patient UUID */}
          <div className="py-1" />
          <div>
            <p className="text-sm text-neutral-400">{patientId}</p>
          </div>

          {/* Patient Full Name and Patient Unique ID */}
          <div className="py-1" />
          <div className="flex flex-row w-full items-center flex-shrink justify-start Xbg-purple-500">
            {/* Patient Full Name */}
            <div className="flex flex-row max-w-[50%] h-full p-2 bg-cyan-200 rounded-md">
              <p className="">
                {patientFullName ? patientFullName : "Nameless Patient"}
              </p>
            </div>
            <div className="px-1" />
            {/* Patient Unique ID */}
            <div className="flex flex-row max-w-[50%] h-full p-2 bg-green-200 rounded-md">
              <p className="break-all">{patientUniqueId}</p>
            </div>
          </div>

          {/* Tags */}
          <div
            className={`flex flex-row items-start justify-start w-full flex-wrap ${
              tags.length > 0 && "pt-2"
            }`}
          >
            {tags.map((tag, index) => (
              <div key={`${tag}-${index}`} className="pr-2 pb-2">
                <div className="flex flex-row items-center justify-start p-2 rounded-md bg-yellow-100">
                  <div className="text-base font-medium text-neutral-900">
                    {tag}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Image container */}
      <div className="flex flex-1 flex-row items-center justify-center md:justify-end w-full h-full pt-4 md:pt-0 Xbg-blue-100">
        <Thumbnail
          thumbnail={thumbnail}
          imageStyle={
            "md:h-80 md:w-80 md:min-h-[20rem] md:min-w-[20rem] rounded-lg"
          }
        />
      </div>
    </div>
  );

  return (
    <>
      {!isRapidEntry ? (
        <Link to={{ pathname: pageLink }}>{buttonContent}</Link>
      ) : (
        <div>{buttonContent}</div>
      )}

      {/* <div className="">{JSON.stringify(row)}</div> */}
    </>
  );
};

const EntryThumbnailRow = ({ row, additionalArgs }) => {
  const images = row.images;

  const entryId = row.original_entry_id;
  const entryType = row.entry_type;
  const entryTypeName = entryNames[entryType];

  const appointmentDate = row.appointment_date;
  const dateModified = row.date_modified;

  const tags = row.classification_tags;
  const summary = row.summary;

  const {
    setEntryBrowserModalOpen,
    // Comparison entry, calculation entries.
    comparisonItems,
    setComparisonItems,
    comparisonItemLocation,
    // Photo Entry.
    photoEntry,
    files,
    setFiles,
    filesDetails,
    setFilesDetails,
  } = additionalArgs;

  const handleImageSelection = (image) => {
    const fetchAndProcessImage = async (imageUrl) => {
      const response = await fetch(imageUrl);
      const blob = await response.blob();
      const file = new File(
        [blob],
        image.image_details.name ? image.image_details.name : `${uuidV4()}.jpg`,
        {
          type: "image/jpeg",
          lastModified: new Date().getTime(),
        }
      );
      return { file };
    };

    fetchAndProcessImage(getImageBaseUrl() + image.original_image).then(
      ({ file }) => {
        if (photoEntry) {
          const filesCopy = cloneDeep(files);
          filesCopy.push({
            file: file,
            previousEntryItem: true,
            uniqueKey: `${uuidV4()}`,
          });

          // console.log({
          //   file: file,
          //   previousEntryItem: true,
          //   uniqueKey: `${uuidV4()}`,
          // });

          const filesDetailsCopy = cloneDeep(filesDetails);
          filesDetailsCopy.push({ caption: "" });
          setFilesDetails(filesDetailsCopy);

          setFiles(filesCopy);
        } else {
          const dataCopy = cloneDeep(comparisonItems);
          dataCopy[comparisonItemLocation.index][
            String(comparisonItemLocation.fileKey)
          ][0] = {
            file: file,
            previousEntryItem: true,
            uniqueKey: `${uuidV4()}`,
          };

          // console.log({
          //   file: file,
          //   previousEntryItem: true,
          //   uniqueKey: `${uuidV4()}`,
          //   uniqueKeyName: file.name,
          // });

          setComparisonItems(dataCopy);
        }
      }
    );
    ScrollEnable();
    setEntryBrowserModalOpen(false);
  };

  return (
    <>
      <div
        className="flex flex-col Xmd:flex-row items-start justify-between w-full h-full p-4 select-none Xbg-orange-500
            border border-neutral-300 rounded-xl"
      >
        {/* Data container */}
        <div className="flex flex-1 flex-row items-start justify-start h-full Xbg-orange-100">
          <div className="flex flex-1 flex-col items-start justify-start text-neutral-800 Xbg-orange-500">
            {/* Entry type */}
            <div className="flex flex-row items-center justify-start w-full Xbg-red-500">
              <p className="text-2xl font-medium">{entryTypeName}</p>
              <div className="px-1" />
              <div className="h-8 w-8">{EntryIcons[entryType]}</div>
            </div>
            <div className="py-1" />

            {/* Appointment Date and Date Modified */}
            <div className="">
              <div className="flex flex-center items-start text-neutral-800">
                <CalendarDaysIcon className="h-7 w-7 min-h-[1.75rem] min-w-[1.75rem] mr-2" />
                <p className={`text-lg font-normal`}>
                  Appointment:{" "}
                  {format(
                    new Date(appointmentDate),
                    " EEEE, MMMM dd, yyyy', at' h:mm a"
                  )}
                </p>
              </div>
              <div className="py-1" />
              <div className="flex flex-center items-start text-neutral-500">
                <PencilSquareIcon className="h-7 w-7 min-h-[1.75rem] min-w-[1.75rem] mr-2" />
                <p className="text-base font-normal">
                  Modified:{" "}
                  {format(
                    new Date(dateModified),
                    " EEEE, MMMM dd, yyyy', at' h:mm a"
                  )}
                </p>
              </div>
            </div>

            {/* Entry UUID */}
            <div className="py-1" />
            <div>
              <p className="text-sm text-neutral-400">{entryId}</p>
            </div>

            {/* Tags */}
            <div
              className={`flex flex-row items-start justify-start w-full flex-wrap ${
                tags.length > 0 && "pt-2"
              }`}
            >
              {tags.map((tag, index) => (
                <div key={`${tag}-${index}`} className="pr-2 pb-2">
                  <div className="flex flex-row items-center justify-start p-2 rounded-md bg-yellow-100">
                    <div className="text-base font-medium text-neutral-900">
                      {tag}
                    </div>
                  </div>
                </div>
              ))}
            </div>
            {/* Summary */}
            {summary && (
              <div className="flex flex-row p-2 bg-blue-100 rounded-md">
                <p>{summary}</p>
              </div>
            )}
          </div>
        </div>
        {/* Image container */}
        <div className="flex flex-1 flex-row flex-wrap items-center justify-start Xmd:justify-end w-full h-full pt-4 Xmd:pt-0 Xbg-blue-100">
          {images.map((image, index) => (
            <div
              key={`${
                image?.thumbnail_image ? image.thumbnail_image : uuidV4()
              }-${index}`}
              className="pr-4 pb-4"
            >
              <div
                onClick={() => {
                  handleImageSelection(image);
                }}
                className="flex flex-row items-center justify-center h-[9rem] w-[9rem]  md:h-[12rem] md:w-[12rem]
                             outline outline-1 outline-neutral-300 Xoutline-red-500 rounded-lg
                                
                             cursor-pointer select-none
                             hover:scale-[105%] active:scale-100 transition-all duration-200
                           hover:border-neutral-800
                                "
              >
                <Thumbnail
                  thumbnail={image.thumbnail_image}
                  imageStyle={
                    "object-scale-down max-h-[8rem] 8max-w-[8rem] md:max-h-[11rem] md:max-w-[11rem] rounded-md"
                  }
                />
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* <div className="">{JSON.stringify(row)}</div> */}
    </>
  );
};

export { EntryRow, PatientRow, EntryThumbnailRow };
